/** Страница «Расширение» — ровно то, что отдаёт `GET /api/ext/status`. */
import { apiFetch } from "@/lib/api";

export type ExtRelease = { version: string | null; date: string | null; size: number | null };

export type ExtTab = {
  channel: "telegram" | "max" | "whatsapp";
  platform: string;
  account_id: string;
  /** Номер своего аккаунта на площадке; пусто — на вкладке не вошли. */
  phone: string;
  /** Имя номера в справочнике CRM (`sMsg_whats_phone`). */
  name: string;
  group: string;
  owner: string;
  /** work — номер компании, personal — группа «Личные», unknown — номера нет в справочнике. */
  kind: "work" | "personal" | "unknown";
  visible: boolean;
  seen: string;
  age: number;
  active: boolean;
};

export type ExtInstall = {
  install: string;
  version: string;
  fresh: boolean;
  browser: string;
  seen: string;
  age: number;
  active: boolean;
  tabs: ExtTab[];
};

/** Браузер Chrome на компьютере сотрудника — из отчёта помощника установки. */
export type ExtHelperBrowser = {
  /** Имя ярлыка браузера. */
  name: string;
  /** Последняя часть папки данных Chrome (полных путей личного компьютера не храним). */
  folder: string;
  /** Имя профиля; пусто — в папке данных он один. */
  profile: string;
  /** none — не установлено, old — загружено из папки, оставшейся от ручной установки. */
  status: "none" | "installed" | "disabled" | "old";
  version: string;
  fresh: boolean;
  running: boolean;
};

/** Компьютер с помощником установки (`docs/specs/2026-10-05-ext-helper-design.md`). */
export type ExtHelper = {
  id: number;
  computer: string;
  seen: string;
  age: number;
  /** Помощник на связи: спрашивал кабинет не больше трёх минут назад — обновление дойдёт само. */
  online: boolean;
  /** Версия самого помощника (её же показывает его окно); пусто — помощник старше 1.1.0. */
  version: string;
  /** Версия в общей папке расширения; пусто — ещё не скачано. */
  folder_version: string;
  fresh: boolean;
  browsers: ExtHelperBrowser[];
};

export type ExtUser = {
  id: number;
  name: string;
  me: boolean;
  active: boolean;
  /** Только нынешние установки: обновлённые в том же браузере и молчащие неделю при свежих — в `hidden`. */
  installs: ExtInstall[];
  /** Сколько прежних установок не показали. */
  hidden: number;
  /** Расширение не отмечалось неделю ни в одном браузере: в `installs` — одна, последняя. */
  gone: boolean;
  helpers: ExtHelper[];
};

/** Канал = номер × площадка: кто на нём сейчас и кто был последним (за 30 дней). */
/** Что сотрудник делал на канале: с кем (имя из шапки диалога, иначе номер или id) и когда. */
export type ExtPhoneDeed = { name: string; phone: string; at: string; chat_id: number | null; deal_id: number | null };

/**
 * Последний открытый диалог и последняя отправка сотрудника на канале — за 7 дней, из телеметрии
 * вкладок. Полей нет вовсе, если у смотрящего нет права на журналы расширения; null — не было.
 */
export type ExtPhoneDeeds = { dialog?: ExtPhoneDeed | null; sent?: ExtPhoneDeed | null };

/**
 * Статус сотрудника в учёте времени CRM сейчас (`sUser.timeStatusID`): работает (время
 * оплачивается), перерыв, ушёл; `since` — с какого момента. null — CRM статуса не отдала.
 */
export type ExtTimeStatus = { name: string; kind: "work" | "break" | "away"; since: string | null };

/** Версия расширения в том браузере сотрудника, где открыт этот канал; `fresh` — не старше выпуска. */
export type ExtPhoneVersion = { version: string; fresh: boolean };

export type ExtPhoneChannel = {
  channel: ExtTab["channel"];
  platform: string;
  active: boolean;
  /** Канал не используем (номер на площадке заблокирован, аккаунта нет): кто и когда отметил. */
  off: { name: string; at: string } | null;
  /** Диалоги канала, где клиент ждёт ответа: сколько, сколько минут ждёт самый давний и его чат. */
  waiting: { count: number; minutes: number | null; chat_id: number | null };
  /**
   * Последний наш ответ клиенту в чатах канала за сутки: когда, сколько секунд назад и кто (пусто —
   * сообщение принёс мост, автора не знаем). Отвечать могут и без расширения: с телефона, из другого браузера.
   */
  replied: { at: string; age: number; author: string } | null;
  /** Вкладка моста этого канала в служебном браузере; моста у канала нет — null. */
  bridge: { state: "ok" | "silent" | "nologin" | "off" | "unused"; name: string; age: number | null } | null;
  /** `errors` — сколько ошибок своего кода расширение прислало с вкладок этого канала за час. */
  users: ({ id: number; name: string; visible: boolean; status: ExtTimeStatus | null; errors?: number } & ExtPhoneVersion & ExtPhoneDeeds)[];
  /**
   * Кто сейчас держит этот же номер в других каналах (`platforms`), а в этом не отмечается. Версия —
   * в том браузере. `tab` — последняя отметка вкладки этого канала в том же браузере: null —
   * расширение на ней не запускалось вовсе (вкладка не открыта или не обновлена после установки).
   */
  beside: ({
    id: number;
    name: string;
    platforms: string[];
    status: ExtTimeStatus | null;
    tab?: { seen: string; age: number } | null;
    errors?: number;
  } & ExtPhoneVersion)[];
  last: ({ id: number; name: string; seen: string; age: number; status: ExtTimeStatus | null } & ExtPhoneVersion & ExtPhoneDeeds) | null;
};

/** Рабочий номер справочника CRM (`sMsg_whats_phone`) и его три канала. */
export type ExtPhone = { phone: string; name: string; group: string; channels: ExtPhoneChannel[] };

/** Вкладка с аккаунтом, номер которого площадка не отдала. */
export type ExtPhoneUnknown = {
  channel: string;
  platform: string;
  account_id: string;
  name: string;
  seen: string;
  age: number;
  active: boolean;
};

export type ExtStatus = {
  release: ExtRelease | null;
  now: string;
  active_seconds: number;
  users: ExtUser[];
  phones: ExtPhone[];
  phones_unknown: ExtPhoneUnknown[];
};

export function loadExtStatus(): Promise<ExtStatus> {
  return apiFetch<ExtStatus>("/api/ext/status");
}

/** Команда установки помощника для Win+R — одна на всех: кто ставит, помощник узнаёт по паролю
 * в своём окне. Адрес кабинета называем явно (`host`): зеркало приходит на сервер с чужим `Host`. */
export function extHelperCommand(): string {
  const { origin, host } = window.location;
  return `powershell -NoProfile -ExecutionPolicy Bypass -Command "irm '${origin}/api/ext/helper/install?host=${host}' | iex"`;
}

/** Та же команда файлом `prokat-helper.cmd`. */
export const extHelperFile = (): string => `/api/ext/helper/file?host=${window.location.host}`;

/** Строка журнала помощника: что он сделал на компьютере сотрудника и что не получилось. */
export type ExtHelperEvent = {
  id: number;
  /** Часы сервера (MSK), без пояса. */
  at: string;
  user_id: number | null;
  /** Пусто — событие до входа в помощник, сотрудник ещё не известен. */
  user: string;
  computer: string;
  install: string;
  code: string;
  ok: boolean;
  text: string;
  data: Record<string, unknown>;
  /** Версия помощника, приславшего событие. */
  script: string;
};

/** `all` — виден журнал всех сотрудников (право управлять полномочиями), иначе только свой. */
export function loadExtHelperEvents(errors: boolean): Promise<{ all: boolean; events: ExtHelperEvent[] }> {
  return apiFetch(`/api/ext/helper/events?limit=300${errors ? "&errors=1" : ""}`);
}

/** Событие журнала действий вкладки: `t` — когда (мс), `k` — что, `n` — сколько раз подряд. */
export type ExtBugEvent = { t?: number; t2?: number; k?: string; n?: number; src?: string; [key: string]: unknown };

/** Сообщение о баге — кнопка «Баг» в шапке панели расширения. */
export type ExtBug = {
  id: number;
  date: string;
  user_id: number;
  user: string;
  channel: string;
  platform: string;
  version: string;
  peer_id: string;
  peer_name: string;
  phone: string;
  text: string;
  env: Record<string, unknown>;
  events: ExtBugEvent[];
};

/** `all` — видны сообщения всех сотрудников (право управлять полномочиями), иначе только свои. */
export type ExtBugs = { all: boolean; bugs: ExtBug[] };

export function loadExtBugs(): Promise<ExtBugs> {
  return apiFetch<ExtBugs>("/api/ext/bugs");
}

/** Строка журнала действий в окне расширения: событие вкладки (`ext`) или то, что по нему сделал
 * сервер (`server`, запись `user_action`). `at` — часы сервера (MSK), без пояса. */
export type ExtEvent = {
  src: "ext" | "server";
  id: number;
  at: string;
  user_id: number | null;
  user: string;
  channel: string;
  version: string;
  account: { id: string; phone: string; name: string };
  peer_id: string;
  peer_name: string;
  phone: string;
  chat_id: number | null;
  deal_id: number | null;
  code: string;
  count: number;
  data: Record<string, unknown>;
  error: string;
};
export type ExtEvents = { all: boolean; events: ExtEvent[] };
export type ExtEventsFilter = { user: string; deal: string; phone: string; day: string; state: boolean };

export function loadExtEvents(f: ExtEventsFilter): Promise<ExtEvents> {
  const q = new URLSearchParams();
  if (f.user) q.set("user", f.user);
  if (f.deal) q.set("deal", f.deal);
  if (f.phone) q.set("phone", f.phone);
  if (f.day) q.set("day", f.day);
  if (f.state) q.set("state", "true");
  return apiFetch<ExtEvents>(`/api/ext/events?${q.toString()}`);
}

/** Мост и его вкладки: строка на мост × площадку; площадка пустая — сам служебный браузер. */
export type ExtBridgeRow = {
  install: string;
  name: string;
  channel: string;
  account_id: string;
  account_phone: string;
  listen: boolean;
  token: boolean;
  version: string;
  /** Что вкладка сообщила о себе: страница не дала подписку и почему. */
  note: string;
  seen: string;
  /** Реестр браузеров: какой номер был на вкладке и когда вход видели последний раз; пусто — не входили. */
  was_phone: string;
  was_title: string;
  was_seen: string;
  /** У строки браузера: время проверки, на которой его порт CDP не ответил; пусто — отвечает. */
  port_down: string;
  /** Площадку в этом браузере не используем: вкладка не слушает, потерей входа не считается. */
  unused: boolean;
  /** Как номер вошедшей вкладки называется у нас (справочник номеров CRM); пусто — не знаем. */
  title: string;
  /** Порт CDP браузера моста (из его имени). */
  port: number | null;
  /** Можно ли из «Чатов» писать клиентам через эту вкладку; `null` — вкладки нет в реестре браузеров. */
  send: boolean | null;
};

/** Шаг набора ответа в служебном браузере: с какой миллисекунды и сколько шёл. */
export type ExtBridgeStep = { step: string; label: string; at: number; ms: number; chars?: number };

/** Заметка набора: что по ходу пошло не так (черновик в поле, набралось не то, состояние вкладки). */
export type ExtBridgeNote = { step: string; label: string; at: number; text: string };

/** Ответ из «Чатов» через служебный браузер: исход, шаги, вторая проверка мостом. */
export type ExtBridgeSend = {
  id: number;
  chat: number;
  channel: string;
  name: string;
  account: string;
  date: string;
  user: string;
  /** typing — набирается · sent — ушло · failed — не ушло · unknown — не подтверждено. */
  state: string;
  error: string;
  tick: string;
  ms: number;
  len: number;
  steps: ExtBridgeStep[];
  notes: ExtBridgeNote[];
  /** Мост принёс это же сообщение: через сколько мс после исхода набора (минус — раньше него). */
  bridge_ms: number | null;
  /** Набор отправку не подтвердил («не подтверждено»), подтвердил её мост. */
  by_bridge: boolean;
  /** Есть снимок вкладки в момент отказа. */
  shot: boolean;
};

/** Событие журнала моста: что вкладка служебного браузера делала и что у неё сломалось. */
export type ExtBridgeEvent = {
  id: number;
  at: string;
  install: string;
  name: string;
  channel: string;
  account_id: string;
  level: "info" | "warn" | "error";
  code: string;
  text: string;
  count: number;
  data: Record<string, unknown>;
  version: string;
};

export type ExtBridgeJournal = { bridges: ExtBridgeRow[]; sends: ExtBridgeSend[]; events: ExtBridgeEvent[] };

export function loadExtBridgeJournal(errors: boolean, hours: number): Promise<ExtBridgeJournal> {
  return apiFetch(`/api/ext/bridge/journal?hours=${hours}${errors ? "&errors=1" : ""}`);
}

/** Числа «Активности моста»: принёс в «Чаты» (`in` от клиентов, `out` наших из браузера и рассылок,
 *  `unsplit` — дни до счётчика 21.10.26, известно только всего) и ответы из «Чатов» через мост. */
export type ExtBridgeCounts = { added: number; in: number; out: number; unsplit: number; sent: number; failed: number };
export type ExtBridgeActivity = {
  from: string;
  to: string;
  total: ExtBridgeCounts;
  days: (ExtBridgeCounts & { date: string })[];
  tabs: (ExtBridgeCounts & { port: number; channel: string; title: string })[];
};

export function loadExtBridgeActivity(from: string, to: string): Promise<ExtBridgeActivity> {
  return apiFetch(`/api/ext/bridge/activity?from=${from}&to=${to}`);
}

/** Разрешить или запретить ответ из «Чатов» через вкладку площадки в служебном браузере. */
export function setExtBridgeSend(port: number, channel: string, send: boolean): Promise<unknown> {
  return apiFetch("/api/ext/bridge/send", { method: "POST", body: JSON.stringify({ port, channel, send }) });
}

/** Используем ли площадку в этом служебном браузере: нет — вкладка не слушает и потерей не считается. */
export function setExtBridgeUse(install: string, channel: string, use: boolean): Promise<unknown> {
  return apiFetch("/api/ext/bridge/use", { method: "POST", body: JSON.stringify({ install, channel, use }) });
}

/** Канал нашего номера не используем (или вернуть в работу): он не «не онлайн», ответа на нём не ждём. */
export function setExtPhoneChannel(phone: string, channel: string, off: boolean): Promise<unknown> {
  return apiFetch("/api/ext/phone-channel", { method: "POST", body: JSON.stringify({ phone, channel, off }) });
}

/** Снимок вкладки служебного браузера в момент, когда ответ не ушёл или не подтвердился. */
export const extBridgeShot = (sendId: number): string => `/api/ext/bridge/shot/${sendId}`;
